/** Regelversion 9: Wahlbeteiligung, Wählerwanderung und Ereignis im Wahlkampf. */
import { useState } from 'react';
import { Panel, Sw } from '../components/ui';
import { useWidth } from '../components/charts';
import { PN, PA, PC } from '../engine/rules';
import { voterFlows, flowMatrix } from '../engine/elect9';
import { de, sg } from '../engine/util';

const mio = (x: number, d = 1) => `${de(x, d)} Mio`;

/** Wahlbeteiligung einer Wahl, mit Vergleich zur vorherigen und der eigenen Mobilisierung. */
export function TurnoutBox({ res, prev }: { res: any; prev?: any }) {
  const T = res?.turnout; if (!T) return null;
  const P = prev?.turnout, d = P ? T.t - P.t : null;
  return (
    <Panel title="Wahlbeteiligung" aside={<b className="num">{de(T.t, 1)} %</b>} className="turnout">
      <div className="hgrid">
        <div><b>Wahlberechtigte</b><span>{mio(T.elig)}</span></div>
        <div><b>Nichtwähler</b><span>{mio(T.nv)}</span></div>
        <div><b>Von euch mobilisiert</b><span>{T.mob >= 1 ? mio(T.mob, 2) : T.mob >= .05 ? `${de(T.mob * 1000, 0)} Tsd.` : 'kaum'}</span></div>
        {d != null && <div><b>Beteiligung</b><span className={d >= 0 ? 'up' : 'down'}>{sg(d, 1)} Punkte</span></div>}
      </div>
      <span className="sub">{T.mob >= 1 ? 'Euer Wahlkampf hat viele Menschen an die Urnen gebracht, die sonst zu Hause geblieben wären.' : T.mob >= .5 ? 'Euer Wahlkampf hat einige Nichtwähler überzeugt.' : 'Euer Wahlkampf hat kaum Nichtwähler erreicht. Starke Themen, eine gute Tour und ein überzeugendes TV-Duell mobilisieren mehr.'}</span>
    </Panel>
  );
}

const nm = (id: string) => (id === 'NV' ? 'Nichtwähler' : PN(id));
const ab = (id: string) => (id === 'NV' ? 'Nichtwähler' : PA(id));
const col = (id: string) => (id === 'NV' ? '#6b7378' : PC(id));
const amt = (x: number) => (x >= 1 ? mio(x) : `${de(x * 1000, 0)} Tsd.`);

/** Beschriftung eines Knotens: Kürzel und Anteil, bei großen Knoten zweizeilig. */
function Lab({ x, y, id, v, sh, h, end }: { x: number; y: number; id: string; v: number; sh: number | null; h: number; end?: boolean }) {
  const val = sh != null ? `${de(sh, 1)} %` : `${de(v, 1)} Mio`, cls = 'sk-lab' + (id === 'YOU' ? ' you' : '');
  if (h < 26) return <text x={x} y={y} textAnchor={end ? 'end' : 'start'} dominantBaseline="middle" className={cls}>{ab(id)} {val}</text>;
  return <text x={x} y={y - 7} textAnchor={end ? 'end' : 'start'} dominantBaseline="middle" className={cls}><tspan x={x}>{ab(id)}</tspan><tspan x={x} dy={15} className="sk-val">{val}</tspan></text>;
}

/**
 * Wählerwanderung als Flussdiagramm: links die Wahl davor, rechts die neue Wahl. Antippen oder
 * Überfahren einer Partei oder eines Stroms hebt ihn hervor und zeigt die Zahlen.
 */
export function FlowBox({ prev, res, from, to }: { prev: any; res: any; from?: number; to?: number }) {
  const F = flowMatrix(prev, res);
  const [sel, setSel] = useState<{ k: 'L' | 'R' | 'F'; i: number; j?: number } | null>(null);
  const [hov, setHov] = useState<typeof sel>(null);
  const [wrap, cw] = useWidth<HTMLDivElement>();
  if (!F) return <div ref={wrap} />;
  const { ids, m } = F, n = ids.length, act = hov || sel;
  const W = Math.min(720, cw), narrow = W < 520, side = narrow ? 84 : 112, top = 30, gap = 6, nodeW = narrow ? 9 : 12, H = narrow ? 360 : 420, lx = side, rx = W - side - nodeW;
  const tot = F.prev.reduce((a, b) => a + b, 0), k = (H - top - gap * (n - 1)) / tot;
  // Knotenpositionen
  const posL: number[] = [], posR: number[] = []; let yl = top, yr = top;
  for (let i = 0; i < n; i++) { posL[i] = yl; yl += F.prev[i] * k + gap; posR[i] = yr; yr += F.next[i] * k + gap; }
  // Ströme: in jedem Knoten in der Reihenfolge der Gegenseite gestapelt
  const offL = posL.slice(), offR = posR.slice(), ribbons: { i: number; j: number; y0: number; y1: number; h: number; v: number }[] = [];
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) { const v = m[i][j]; if (v <= 0) continue; const h = v * k; ribbons.push({ i, j, y0: offL[i], y1: 0, h, v }); offL[i] += h; }
  for (let j = 0; j < n; j++) for (const r of ribbons.filter((x) => x.j === j)) { r.y1 = offR[j]; offR[j] += r.h; }
  const on = (r: { i: number; j: number }) => !act || (act.k === 'L' ? r.i === act.i : act.k === 'R' ? r.j === act.i : r.i === act.i && r.j === act.j);
  const cx = (lx + nodeW + rx) / 2;
  const path = (r: typeof ribbons[0]) => {
    const x0 = lx + nodeW, x1 = rx;
    return `M${x0},${r.y0} C${cx},${r.y0} ${cx},${r.y1} ${x1},${r.y1} L${x1},${r.y1 + r.h} C${cx},${r.y1 + r.h} ${cx},${r.y0 + r.h} ${x0},${r.y0 + r.h} Z`;
  };
  const toggle = (x: NonNullable<typeof sel>) => setSel((s0) => (s0 && s0.k === x.k && s0.i === x.i && s0.j === x.j ? null : x));
  // Text zur Auswahl
  let info: JSX.Element;
  if (act?.k === 'F') {
    const v = m[act.i][act.j!];
    info = act.i === act.j ? <>{nm(ids[act.i])}: <b>{amt(v)}</b> haben {ids[act.i] === 'NV' ? 'wieder nicht gewählt' : 'wieder gleich gewählt'}.</> : <>Von {nm(ids[act.i])} zu {nm(ids[act.j!])}: <b>{amt(v)}</b></>;
  } else if (act) {
    const i = act.i, L = act.k === 'L';
    const list = ids.map((id, x) => ({ id, v: L ? m[i][x] : m[x][i] })).filter((x) => x.v >= .02 && x.id !== ids[i]).sort((a, b) => b.v - a.v).slice(0, 4);
    info = <>{nm(ids[i])} {L ? `(${from ?? 'davor'})` : `(${to ?? 'jetzt'})`}: <b>{amt(L ? F.prev[i] : F.next[i])}</b>{list.length ? <> · {L ? 'gewechselt zu' : 'gekommen von'} {list.map((x, z) => <span key={x.id}>{z ? ', ' : ''}{ab(x.id)} {amt(x.v)}</span>)}</> : null}</>;
  } else info = <>Tippe auf eine Partei oder einen Strom, um zu sehen, wohin die Stimmen gewandert sind.</>;
  const fl = voterFlows(prev, res).slice(0, 4);
  return (
    <Panel title="Wählerwanderung" aside="Schätzung, wie nach echten Wahlen" className="flows">
      <div className="sankey-wrap" ref={wrap}>
        <svg className="sankey" viewBox={`0 0 ${W} ${H + 8}`} role="img" aria-label="Wählerwanderung zwischen den beiden Wahlen" onMouseLeave={() => setHov(null)}>
          <text x={lx + nodeW / 2} y={18} textAnchor="middle" className="sk-year">{from ?? 'Wahl davor'}</text>
          <text x={rx + nodeW / 2} y={18} textAnchor="middle" className="sk-year">{to ?? 'Neue Wahl'}</text>
          {ribbons.filter((r) => r.v >= .015).map((r, z) => (
            <path key={z} d={path(r)} fill={col(ids[r.i])} opacity={on(r) ? (act ? .7 : .45) : .07} className="sk-flow"
              onMouseEnter={() => setHov({ k: 'F', i: r.i, j: r.j })} onClick={() => toggle({ k: 'F', i: r.i, j: r.j })}><title>{`${nm(ids[r.i])} → ${nm(ids[r.j])}: ${amt(r.v)}`}</title></path>))}
          {ids.map((id, i) => (
            <g key={'l' + id} className="sk-node" onMouseEnter={() => setHov({ k: 'L', i })} onClick={() => toggle({ k: 'L', i })}>
              <rect x={lx} y={posL[i]} width={nodeW} height={Math.max(1, F.prev[i] * k)} fill={col(id)} />
              <Lab x={lx - 6} y={posL[i] + F.prev[i] * k / 2} end id={id} v={F.prev[i]} sh={id === 'NV' ? null : prev.share[id] ?? 0} h={F.prev[i] * k} />
            </g>))}
          {ids.map((id, i) => (
            <g key={'r' + id} className="sk-node" onMouseEnter={() => setHov({ k: 'R', i })} onClick={() => toggle({ k: 'R', i })}>
              <rect x={rx} y={posR[i]} width={nodeW} height={Math.max(1, F.next[i] * k)} fill={col(id)} />
              <Lab x={rx + nodeW + 6} y={posR[i] + F.next[i] * k / 2} id={id} v={F.next[i]} sh={id === 'NV' ? null : res.share[id] ?? 0} h={F.next[i] * k} />
            </g>))}
        </svg>
      </div>
      <p className="sk-info" aria-live="polite">{info}</p>
      {fl.length > 0 && <span className="sub">Für {PN('YOU')}: {fl.map((x, z) => <span key={x.id}>{z ? ' · ' : ''}{x.id !== 'NV' && <Sw id={x.id} />}{ab(x.id)} <b className={x.n > 0 ? 'up' : 'down'}>{x.n > 0 ? '+' : '−'}{amt(Math.abs(x.n))}</b></span>)}</span>}
    </Panel>
  );
}

/** Ereignis im Wahlkampf (Hinweis zu Beginn). */
export function CampEventNote({ ev }: { ev: any }) {
  if (!ev) return null;
  return (
    <div className="msg campev"><b>Ereignis im Wahlkampf: {ev.t}.</b> {ev.s} Umfragen: {PN(ev.pid)} {sg(ev.boost, 1)} Punkte.{ev.pen < 0 ? ` Euch kostet das ${de(-ev.pen, 1)} Punkte, weil eure Position nicht zur neuen Stimmung passt.` : ev.pen > 0 ? ` Das hilft auch euch ein wenig.` : ''}</div>
  );
}
